import { DownOutlined, MailOutlined, MessageOutlined } from "@ant-design/icons"
import { Button, Drawer, Dropdown, Result } from "antd"
import React, {
  Component,
  type ComponentType,
  type ReactNode,
  Suspense,
  lazy,
  useEffect,
  useState
} from "react"
import { useTranslation } from "react-i18next"

import { isChunkLoadError } from "../../../utils/handlers/is-chunk-load-error.ts"
import { reloadForChunkError } from "../../../utils/handlers/reload-for-chunk-error.ts"
import { useCommunicationRights } from "../communication-permissions.ts"
import type { Channel } from "../types/compose.types.ts"
import {
  channelMenuLabel,
  notConfiguredKey,
  useChannelsConfigured
} from "../hub/channel-configured.tsx"
import type { ComposerPrefill } from "../hub/hub-context.ts"
import { type ComposerTarget, targetToPrefill } from "./composer-target.ts"
import { isOffline, loadComposer } from "./load-composer.ts"

type ComposerDrawerProps = {
  prefill: ComposerPrefill
  onClose: () => void
}

type LoadFailure = "offline" | "failed"

// A fresh lazy component per attempt: React.lazy keeps a rejected import
// for good, so Try again needs a new one.
const lazyComposer = (): ComponentType<ComposerDrawerProps> =>
  lazy(loadComposer)

class ComposerLoadBoundary extends Component<
  { onError: (error: unknown) => void; children: ReactNode },
  { failed: boolean }
> {
  state = { failed: false }

  static getDerivedStateFromError() {
    return { failed: true }
  }

  componentDidCatch(error: unknown) {
    this.props.onError(error)
  }

  render() {
    return this.state.failed ? null : this.props.children
  }
}

// Mounted by Suspense only while the composer's file is still on its way.
const ComposerLoading = ({
  onLoading
}: {
  onLoading: (loading: boolean) => void
}) => {
  useEffect(() => {
    onLoading(true)
    return () => onLoading(false)
  }, [onLoading])
  return null
}

const ComposerLoadNotice = ({
  failure,
  onRetry,
  onClose
}: {
  failure: LoadFailure
  onRetry: () => void
  onClose: () => void
}) => {
  const { t } = useTranslation()
  return (
    <Drawer open onClose={onClose} width={420} destroyOnHidden>
      <Result
        status="warning"
        title={
          failure === "offline"
            ? t("communication.composer.loadOffline")
            : t("communication.composer.loadFailed")
        }
        extra={
          <Button type="primary" onClick={onRetry}>
            {t("communication.composer.loadRetry")}
          </Button>
        }
      />
    </Drawer>
  )
}

/**
 * Opens the shared composer, fetching its file on first use. `element` must
 * be rendered for the composer (or its load notice) to appear.
 */
export const useComposerLauncher = (onClosed?: () => void) => {
  const [prefill, setPrefill] = useState<ComposerPrefill | null>(null)
  const [ComposerDrawer, setComposerDrawer] =
    useState<ComponentType<ComposerDrawerProps> | null>(null)
  const [loading, setLoading] = useState(false)
  const [failure, setFailure] = useState<LoadFailure | null>(null)
  const [attempt, setAttempt] = useState(0)
  // Vite never asks again for a stylesheet it already tried, so an import
  // that failed part-way would open the composer unstyled. Once one has
  // failed, Try again reloads the app instead.
  const [importFailed, setImportFailed] = useState(false)

  const startLoading = () => {
    if (isOffline()) {
      setFailure("offline")
      return
    }
    if (importFailed) {
      reloadForChunkError()
      return
    }
    setFailure(null)
    setAttempt((n) => n + 1)
    setComposerDrawer(() => lazyComposer())
  }

  const open = (next: ComposerPrefill) => {
    setPrefill(next)
    if (!ComposerDrawer) startLoading()
  }

  const close = () => {
    setPrefill(null)
    setFailure(null)
    onClosed?.()
  }

  const handleLoadError = (error: unknown) => {
    setImportFailed(true)
    if (isOffline()) {
      setFailure("offline")
    } else if (isChunkLoadError(error)) {
      reloadForChunkError()
    } else {
      setFailure("failed")
    }
    setComposerDrawer(null)
  }

  const element = (
    <>
      {prefill && failure && (
        <ComposerLoadNotice
          failure={failure}
          onRetry={startLoading}
          onClose={close}
        />
      )}
      {ComposerDrawer && (
        <ComposerLoadBoundary key={attempt} onError={handleLoadError}>
          <Suspense fallback={<ComposerLoading onLoading={setLoading} />}>
            {prefill && <ComposerDrawer prefill={prefill} onClose={close} />}
          </Suspense>
        </ComposerLoadBoundary>
      )}
    </>
  )

  return { open, loading, element }
}

export const CommunicateButton = ({
  target,
  size = "large",
  label,
  onClosed
}: {
  target: ComposerTarget | null
  size?: "middle" | "large"
  label?: string
  onClosed?: () => void
}) => {
  const { t } = useTranslation()
  const rights = useCommunicationRights()
  const composer = useComposerLauncher(onClosed)
  // Read when the menu first opens, not on every page that shows the button.
  const [menuOpened, setMenuOpened] = useState(false)
  const configured = useChannelsConfigured(menuOpened)
  if (!rights.send || !target) return null
  const reasonFor = (picked: Channel) =>
    configured[picked] === false ? t(notConfiguredKey(picked)) : null
  const text = label ?? t("communication.composer.communicate")

  const choose = (picked: Channel) =>
    composer.open(targetToPrefill({ ...target, channel: picked }))

  return (
    <>
      <Dropdown
        trigger={["click"]}
        disabled={composer.loading}
        onOpenChange={(open) => {
          if (open) setMenuOpened(true)
        }}
        menu={{
          items: [
            {
              key: "email",
              icon: <MailOutlined aria-hidden />,
              disabled: !!reasonFor("email"),
              label: channelMenuLabel(
                t("communication.composer.sendEmail"),
                reasonFor("email")
              )
            },
            {
              key: "sms",
              icon: <MessageOutlined aria-hidden />,
              disabled: !!reasonFor("sms"),
              label: channelMenuLabel(
                t("communication.composer.sendSms"),
                reasonFor("sms")
              )
            }
          ],
          onClick: ({ key }) => choose(key as Channel)
        }}
      >
        <Button
          type="primary"
          aria-label={t("communication.composer.communicate")}
          size={size}
          className="rounded-lg"
          loading={composer.loading}
        >
          {text} <DownOutlined aria-hidden />
        </Button>
      </Dropdown>
      {composer.element}
    </>
  )
}
